import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import authService from "../services/auth";
import { User } from "../services/api";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  completeOnboarding: () => void;
  updateUser: (updates: Partial<User>) => void;
  error: string | null;
  clearError: () => void;
  initiateGoogleAuth: () => void;
  handleOAuthCallback: (token: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authRetryCount, setAuthRetryCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  // Check authentication status on initial load
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Checking authentication status...');

        // Check if user has valid authentication token
        if (authService.isAuthenticated()) {
          console.log('🔑 Token found, verifying with backend...');

          try {
            const authStatus = await authService.getAuthStatus();

            if (authStatus.authenticated && authStatus.user) {
              setUser(authStatus.user);
              setAuthRetryCount(0); // Reset retry count on success
              console.log('✅ User authenticated:', authStatus.user.email);
            } else {
              // Token exists but auth status says not authenticated
              console.log('⚠️ Auth status indicates not authenticated, clearing user only...');
              setUser(null);
              // Don't call logout() here as it makes a backend request
            }
          } catch (authError) {
            console.error('❌ Auth status check failed:', authError);

            // Retry logic for network errors
            if (authRetryCount < 2) {
              console.log(`🔄 Retrying auth check (attempt ${authRetryCount + 1}/2)...`);
              setAuthRetryCount(prev => prev + 1);

              // Retry after a short delay
              setTimeout(() => {
                checkAuth();
              }, 1000);
              return;
            } else {
              console.log('❌ Max auth retries reached, clearing user state only...');
              setUser(null);
              // Don't call logout() to avoid backend request
            }
          }
        } else {
          setUser(null);
          console.log('ℹ️ No authentication token found');
        }
      } catch (error) {
        console.error('❌ Auth check failed:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
        console.log('🏁 Auth check completed');
      }
    };

    // Check auth immediately without delay
    checkAuth();
  }, []);

  // Periodic token validation (every 5 minutes)
  useEffect(() => {
    if (!user) return;

    const validateToken = async () => {
      try {
        const authStatus = await authService.getAuthStatus();
        if (!authStatus.authenticated) {
          console.log('⚠️ Token expired, logging out...');
          await logout();
        }
      } catch (error) {
        console.error('❌ Token validation failed:', error);
        await logout();
      }
    };

    // Validate token every 5 minutes
    const intervalId = setInterval(validateToken, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [user]);

  // Handle route protection
  useEffect(() => {
    if (!isLoading) {
      console.log('🛡️ Route protection check:', {
        user: user?.email,
        path: location.pathname,
        isAuthenticated: !!user,
        hasToken: authService.isAuthenticated()
      });

      // Public routes that don't require authentication
      const publicRoutes = ["/login", "/signup", "/signup/verify-otp", "/onboarding", "/auth/callback"];

      // If user is not authenticated and trying to access a protected route
      if (!user && !publicRoutes.includes(location.pathname)) {
        // Check if we have a token but user is null (might be a temporary network issue)
        if (authService.isAuthenticated()) {
          console.log('🔄 Token exists but user is null, attempting refresh...');

          // Try to refresh auth once
          refreshAuth().then(() => {
            // If refresh fails, the user will still be null and this will run again
            if (!user) {
              console.log('🔄 Auth refresh completed, checking again...');
            }
          });
          return;
        }

        console.log('🚫 No token found, redirecting to login');
        navigate("/login", { replace: true });
        return;
      }

      // If user is authenticated and trying to access auth routes (login/signup)
      const authRoutes = ["/login", "/signup", "/signup/verify-otp"];
      if (user && authRoutes.includes(location.pathname)) {
        console.log('✅ Authenticated user accessing auth route, redirecting...');

        // Admin users bypass onboarding and go directly to dashboard
        if (user.email === 'admin@bizamply.com') {
          console.log('👑 Admin user, redirecting to dashboard');
          navigate("/dashboard", { replace: true });
        } else if (user.firstimeSetup === 0) {
          console.log('🚀 First-time user, redirecting to onboarding');
          navigate("/onboarding", { replace: true });
        } else {
          console.log('🏠 Returning user, redirecting to dashboard');
          navigate("/dashboard", { replace: true });
        }
      }
    }
  }, [user, isLoading, location.pathname, navigate]);

  // Login function
  const login = async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('🔐 Attempting login...');

      const response = await authService.login({ email, password });

      if (response.success && response.user) {
        setUser(response.user);
        console.log('✅ Login successful, redirecting...');

        // Redirect based on onboarding status
        if (response.user.email === 'admin@bizamply.com') {
          navigate('/dashboard');
        } else if (response.user.firstimeSetup === 0) {
          navigate('/onboarding');
        } else {
          navigate('/dashboard');
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (email: string, password: string, name: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('📝 Attempting registration...');

      const response = await authService.register({ email, password, name });

      if (response.success && response.user) {
        setUser(response.user);
        console.log('✅ Registration successful, redirecting to onboarding...');
        navigate('/onboarding');
      } else {
        throw new Error(response.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('❌ Registration failed:', error);
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      console.log('🚪 Logging out...');

      // Call backend logout endpoint
      await authService.logout();

      // Clear all user state
      setUser(null);
      setError(null);

      // Clear specific cached data but preserve some settings
      const keysToRemove = [
        'auth_token',
        'csrf_token',
        'onboarding_data',
        'onboarding_brands',
        'onboarding_step',
        'brandIds',
        'serviceConnections',
        'bizamplyId'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      console.log('✅ Logout successful');

      // Navigate to login with replace to prevent back navigation
      navigate('/login', { replace: true });
    } catch (error: any) {
      console.error('❌ Logout error:', error);

      // Still clear user state even if logout request fails
      setUser(null);
      setError(null);

      // Clear storage even on error
      const keysToRemove = [
        'auth_token',
        'csrf_token',
        'onboarding_data',
        'onboarding_brands',
        'onboarding_step',
        'brandIds',
        'serviceConnections',
        'bizamplyId'
      ];

      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      // Navigate to login
      navigate('/login', { replace: true });
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh authentication
  const refreshAuth = async (): Promise<void> => {
    try {
      console.log('🔄 Manually refreshing authentication...');
      setIsLoading(true);

      if (authService.isAuthenticated()) {
        const authStatus = await authService.getAuthStatus();

        if (authStatus.authenticated && authStatus.user) {
          setUser(authStatus.user);
          console.log('✅ Auth refresh successful:', authStatus.user.email);
        } else {
          console.log('⚠️ Auth refresh failed, clearing user');
          setUser(null);
        }
      } else {
        console.log('ℹ️ No token found during refresh');
        setUser(null);
      }
    } catch (error) {
      console.error('❌ Auth refresh failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Complete onboarding
  const completeOnboarding = () => {
    if (user) {
      const updatedUser = { ...user, firstimeSetup: 1 };
      setUser(updatedUser);
      navigate('/dashboard');
    }
  };

  // Update user data (for profile updates)
  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      console.log('✅ User data updated in context:', updates);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  // Google Auth functions
  const initiateGoogleAuth = () => {
    const backendUrl = import.meta.env.VITE_API_URL || 'https://dashboard.bizamply.com';
    window.location.href = `${backendUrl}/auth/google`;
  };

  const handleOAuthCallback = async (token: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the auth service to handle the OAuth callback
      const user = await authService.handleOAuthCallback(token);

      if (user) {
        setUser(user);

        // Handle redirection based on user status
        setTimeout(() => {
          if (user.email === 'admin@bizamply.com') {
            navigate('/dashboard');
          } else if (user.firstimeSetup === 0) {
            navigate('/onboarding');
          } else {
            navigate('/dashboard');
          }
        }, 100);
      } else {
        throw new Error('Failed to authenticate with OAuth token');
      }
    } catch (error: any) {
      setError(error.message || 'Authentication failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        login,
        register,
        logout,
        refreshAuth,
        completeOnboarding,
        updateUser,
        error,
        clearError,
        initiateGoogleAuth,
        handleOAuthCallback
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
